import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ContentFormFields from "./ContentFormFields";
import ContentUploadPanel from "./ContentUploadPanel";
import Button from "../Common/Button";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast } from "../../utils/toast";

const ContentForm = ({
    formData,
    onSubmit,
    isError,
    isEditing,
    showActions,
    isSubmitting = false,
    onMediaUploaded,
}) => {
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedMedia, setUploadedMedia] = useState({
        mediaUrl: formData?.mediaUrl || "",
        cloudflareId: formData?.cloudflareId || "",
        thumbnailUrl: formData?.thumbnailUrl || "",
        thumbnailCloudflareId: formData?.thumbnailCloudflareId || "",
    });
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            title: "",
            platform: null,
            link: "",
            fileType: null,
            postType: null,
            caption: "",
            acceptedTerms: false,
        },
    });
    const selectedFileType = useWatch({ control, name: "fileType" });
    const selectedFileTypeValue = selectedFileType?.value || "";
    const uploadHelperText = selectedFileTypeValue === "Video"
        ? t("CONTENT_UPLOAD_HELPER_VIDEO")
        : selectedFileTypeValue === "Image"
            ? t("CONTENT_UPLOAD_HELPER_IMAGE")
            : t("CONTENT_UPLOAD_HELPER_DEFAULT");

    useEffect(() => {
        if (!formData) return;
        reset({
            title: formData.title || "",
            platform: formData.platform
                ? { value: formData.platform, label: formData.platform }
                : null,
            link: formData.link || "",
            fileType: formData.fileType
                ? { value: formData.fileType, label: formData.fileType }
                : null,
            postType: formData.postType
                ? { value: formData.postType, label: formData.postType }
                : null,
            caption: formData.caption || "",
            acceptedTerms: formData.acceptedTerms || false,
        });
    }, [formData, reset]);

    const handleFormSubmit = (values) => {
        const normalized = {
            ...values,
            platform: values.platform?.value || "",
            fileType: values.fileType?.value || "",
            postType: values.postType?.value || "",
        };

        onSubmit?.({
            ...normalized,
            ...uploadedMedia,
        });
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors, t("CONTENT_VALIDATION_FILL_REQUIRED"));
    };

    const canSubmit = isValid && !isSubmitting && !isUploading && Boolean(uploadedMedia.mediaUrl);

    return (
        <div className="grid items-stretch gap-8 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
                <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}>
                    <ContentFormFields
                        mode="edit"
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        showTerms
                    />
                    {showActions && (
                        <div className="mt-4">
                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                className="w-full"
                            >
                                {isSubmitting ? t("COMMON_SUBMITTING") : isEditing ? t("CONTENT_BTN_SAVE_CHANGES") : t("CONTENT_BTN_SUBMIT")}
                            </Button>
                        </div>
                    )}
                </form>
            </div>
            <div className="order-1 flex flex-col lg:order-2">
                <ContentUploadPanel
                    showError={isError}
                    initialPreview={formData?.preview || formData?.mediaUrl}
                    selectedFileType={selectedFileTypeValue}
                    uploadEnabled={Boolean(selectedFileTypeValue)}
                    helperText={uploadHelperText}
                    onUploadStart={() => setIsUploading(true)}
                    onClear={() => {
                        setUploadedMedia({ mediaUrl: "", cloudflareId: "", thumbnailUrl: "", thumbnailCloudflareId: "" });
                    }}
                    onUploadError={(message) => {
                        setIsUploading(false);
                        showErrorToast(message || t("CONTENT_UPLOAD_ERROR_TYPE_MISMATCH"));
                    }}
                    onUploadComplete={(result) => {
                        setIsUploading(false);
                        const prevMedia = uploadedMedia;
                        const mediaUrl = result.mediaUrl || "";
                        const cloudflareId = result.cloudflareId || "";
                        const isVideo = result.fileType === "Video";
                        const thumbnailUrl = isVideo && cloudflareId
                            ? `https://videodelivery.net/${cloudflareId}/thumbnails/thumbnail.jpg`
                            : mediaUrl;

                        setUploadedMedia({
                            mediaUrl,
                            cloudflareId,
                            thumbnailUrl,
                            thumbnailCloudflareId: isVideo ? cloudflareId : "",
                        });
                        onMediaUploaded?.(
                            {
                                mediaUrl,
                                cloudflareId,
                                thumbnailUrl,
                                thumbnailCloudflareId: isVideo ? cloudflareId : "",
                                fileType: result.fileType,
                            },
                            prevMedia
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default ContentForm;
